import ChipTip from '@/components/ChipTip';
import { isFixedRateAsset, fixedRateTerms } from '@/components/FixedRateAssetCard';

// Oct 2026 — compact call-risk chip for Watchlist / Portfolio / Dashboard
// rows. Only renders when there is something to act on: callable now,
// issuer's choice, or a call date within 12 months — and always when the
// share trades above par with a known call term.
function fmtMonth(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function preferredCallRisk(stock) {
  if (!isFixedRateAsset(stock)) return null;
  const t = fixedRateTerms(stock);
  if (!t.parKnown || !(t.px > 0)) return null;
  const par = `$${t.par.toFixed(2)}`;
  const prem = t.px - t.par;
  const premTxt = `$${Math.abs(prem).toFixed(2)} ${prem > 0 ? 'above' : 'below'} par`;
  if (t.callPast && t.abovePar) {
    return { level: 'danger', label: 'Callable · above par', text: `${stock.ticker} is past its call date (${fmtMonth(t.callIso)}) and trades ${premTxt}. The issuer can redeem at ${par} any time — an instant loss of $${prem.toFixed(2)}/share.` };
  }
  if (t.issuerChoice && t.abovePar) {
    return { level: 'warn', label: 'Callable any time · above par', text: `${stock.ticker} is redeemable at ${par} at the issuer's choice (no fixed call date) and trades ${premTxt}.` };
  }
  if (t.callPast) {
    return { level: 'info', label: 'Callable now', text: `${stock.ticker} is past its call date (${fmtMonth(t.callIso)}). It trades ${premTxt}, so a redemption at ${par} would pay you the gap.` };
  }
  if (t.callIso) {
    const days = (new Date(`${t.callIso}T00:00:00Z`).getTime() - Date.now()) / 86400000;
    if (t.abovePar && days <= 365) {
      return { level: 'warn', label: `Call ${fmtMonth(t.callIso)} · above par`, text: `${stock.ticker} becomes callable at ${par} on ${t.callIso} (${Math.round(days)} days) and trades ${premTxt} — the premium is at risk.` };
    }
    if (t.abovePar) {
      return { level: 'soft', label: `Above par · call ${fmtMonth(t.callIso)}`, text: `${stock.ticker} trades ${premTxt}. Call protection lasts until ${t.callIso}, after which the issuer may redeem at ${par}.` };
    }
  }
  return null;
}

const CLS = {
  danger: 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300 border-red-400/60',
  warn: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-400/60',
  info: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-400/60',
  soft: 'bg-muted text-muted-foreground border-border',
};

export default function PreferredCallRiskChip({ stock, onOpenTicker }) {
  const risk = preferredCallRisk(stock);
  if (!risk) return null;
  return (
    <ChipTip text={risk.text} ticker={stock.ticker} onOpenTicker={onOpenTicker} testId={`pref-call-risk-chip-${stock.ticker}`}>
      <span className={`inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide border whitespace-nowrap cursor-help ${CLS[risk.level]}`}>
        {risk.level === 'danger' ? '⚠ ' : ''}{risk.label}
      </span>
    </ChipTip>
  );
}
